import parseUrl from 'url-parse';

import bigQueryLogo from './logos/BigQuery_logo.png'
import googleSheetsLogo from './logos/GoogleSheets_logo.png'
import {IDatabaseKind, IDatabaseUri} from '../models';

export const databaseTypes: IDatabaseKind[] = [
  {
    name: 'Amazon Athena',
    backend: 'awsathena',
    port: 0
  },
  {
    name: 'Apache Impala',
    backend: 'impala',
    port: 20150
  },
  {
    name: 'Apache Kylin',
    backend: 'kylin',
    port: 7070
  },
  {
    name: 'Big Query',
    backend: 'bigquery',
    port: 0,
    logo: bigQueryLogo
  },
  {
    name: 'Clickhouse',
    backend: 'clickhouse',
    port: 8123
  },
  {
    name: 'Databricks',
    backend: 'databricks',
    port: 443
  },
  {
    name: 'Exasol',
    backend: 'exa',
    port: 8563
  },
  {
    name: 'Firebird',
    backend: 'firebird',
    port: 3050
  },
  {
    name: 'Google Sheets SQL',
    backend: 'gsheets',
    port: 0,
    logo: googleSheetsLogo,
  },
  {
    name: 'MariaDB',
    backend: 'mariadb',
    port: 3306
  },
  {
    name: 'Microsoft SQL Server',
    backend: 'mssql',
    port: 1433
  },
  {
    name: 'MySQL',
    backend: 'mysql',
    port: 3306
  },
  {
    name: 'Mongo BI',
    backend: 'mongobi',
    port: 3307
  },
  {
    name: 'Oracle',
    backend: 'oracle',
    port: 1521
  },
  {
    name: 'PostgreSQL',
    backend: 'postgresql',
    port: 5432
  },
  {
    name: 'Redshift',
    backend: 'redshift',
    port: 5439
  },
  {
    name: 'Snowflake',
    backend: 'snowflake',
    port: 0
  },
  {
    name: 'Rockset',
    backend: 'rockset',
    port: 0
  },
  {
    name: 'Vertica',
    backend: 'vertica',
    port: 5433
  },
];

export const popularDatabases = databaseTypes.filter(type => {
  return (
    type.backend === 'awsathena'
    || type.backend === 'bigquery'
    || type.backend === 'clickhouse'
    || type.backend === 'databricks'
    || type.backend === 'exa'
    || type.backend === 'gsheets'
    || type.backend === 'mongobi'
    || type.backend === 'mssql'
    || type.backend === 'mysql'
    || type.backend === 'oracle'
    || type.backend === 'postgresql'
    || type.backend === 'redshift'
    || type.backend === 'rockset'
    || type.backend === 'snowflake'
    || type.backend === 'vertica'
  );
});

export const isBigQuery = (engine?: string) => engine === 'bigquery';
export const isSnowflake = (engine?: string) => engine === 'snowflake';
export const isClickhouse = (engine?: string) => engine === 'clickhouse';
export const isRockset = (engine?: string) => engine === 'rockset';
export const isGoogleSheets = (engine?: string) => engine === 'gsheets';
export const isVertica = (engine?: string) => engine === 'vertica';
export const isMongoBi = (engine?: string) => engine === 'mongobi';
export const isAmazonAthena = (engine?: string) => engine === 'awsathena';
export const isOracle = (engine?: string) => engine === 'oracle';
export const isDatabricks = (engine?: string) => engine === 'databricks';

function buildRocksetConnectionUrl(api_key?: string, region?: string) {
  return `rockset://apikey:${api_key}@${region}/`;
}

function exasolParams(schema?: string): string {
  /*
   * Take Exasol params from schema if it has params, otherwise use default params
   */
  let intTypesInResultsIfPossible = 'INTTYPESINRESULTSIFPOSSIBLE=y';
  let driver = 'driver=EXAODBC';

  const defaultParams = `?${intTypesInResultsIfPossible}&${driver}`;

  if (!schema) {
    return defaultParams;
  }

  if (schema.indexOf('?') >= 0) {
    // has params
    if (schema.indexOf('INTTYPESINRESULTSIFPOSSIBLE=') >= 0) {
      intTypesInResultsIfPossible = '';
    }
    if (schema.indexOf('driver=') >= 0) {
      // we don't allow to change driver
      schema = schema.replace(/driver=[^&]*/, '');
    }
    // remove schema name
    schema = schema.replace(/^[^?]*/, '');

    const params = `${schema}` +
      `${intTypesInResultsIfPossible ? '&' + intTypesInResultsIfPossible : ''}` +
      `${driver ? ('&' + driver) : ''}`;
    return params
      .replace("&&", "&")
      .replace("?&", "?");
  }
  return defaultParams;
}

function buildSnowflakeConnectionUri(
  username?: string,
  password?: string,
  database?: string,
  account?: string, /* account + region */
  schema?: string,
  warehouse?: string,
  role?: string
) {
  const hasParams = (warehouse || role) ? '?' : '';
  return ("snowflake://" +
    `${username}${password ? (':' + password) : ''}` +
    `@${account}` +
    `/${database}/${schema}${hasParams}` +
    `${warehouse ? ('warehouse=' + warehouse) : ''}` +
    `${role ? ('&role=' + role) : ''}`);
}

function buildAwsAthenaConnectionUri(
  aws_access_key_id?: string,
  aws_secret_access_key?: string,
  endpoint?: string,
  schema?: string,
  s3_staging_directory?: string,
  workgroup?: string
) {
  const hasParams = (workgroup || s3_staging_directory) ? '?' : '';
  return ("awsathena+rest://" +
    `${aws_access_key_id}:${aws_secret_access_key}` +
    `@${endpoint}:443` +
    `/${schema}${hasParams}` +
    `${workgroup ? ('work_group=' + workgroup) : ''}` +
    `${s3_staging_directory ? ('&s3_staging_dir=' + s3_staging_directory) : ''}`);
}

export function buildConnectionUri(
  type: string,
  hostname?: string,
  port?: number,
  username?: string,
  password?: string,
  database?: string,
  account?: string,
  schema?: string,
  warehouse?: string,
  role?: string,
  protocol?: string,
  region?: string,
  api_key?: string,
  aws_access_key_id?: string,
  aws_secret_access_key?: string,
  endpoint?: string,
  s3_staging_directory?: string,
  workgroup?: string,
  token?: string,
  http_path?: string,
) {
  /*
   * account, schema, warehouse, role are only used for Snowflake connection
   *
   * protocol is only used for Clickhouse connection
   *
   * api_key, region is only used for Rockset connection
   */

  let connType = ''; // connection type + python driver
  const setPort = (port: number | undefined, defaultPort: number) => port ? port : defaultPort;
  let parameters = '';

  switch (type.toLowerCase()) {
    case 'awsathena':
      connType = 'awsathena+rest';
      return buildAwsAthenaConnectionUri(aws_access_key_id, aws_secret_access_key, endpoint, schema, s3_staging_directory, workgroup);
    case 'bigquery':
      connType = 'bigquery';
      return connType + '://';
    case 'clickhouse':
      connType = 'clickhouse';
      port = setPort(port, 8123);

      parameters = `?protocol=${protocol || 'https'}`;
      break;
    case 'databricks':
      connType = 'databricks';
      port = setPort(port, 443);
      return (
        connType + '://' +
        `token:${token}` +
        `@${hostname}:${port}` +
        `/${database}?http_path=${http_path}`
      );
    case 'exa':
      connType = 'exa+pyodbc';
      port = setPort(port, 8563);
      parameters = exasolParams(database);
      database = (database || '').replace(/\?[^?]*$/, ''); // remove params
      break;
    case 'firebird':
      connType = 'firebird+fdb';
      port = setPort(port, 3050);
      break;
    case 'gsheets':
      connType = 'gsheets';
      return connType + '://';
    case 'kylin':
      connType = 'kylin'; // kylinpy
      port = setPort(port, 7070);
      break;
    case 'impala':
      connType = 'impala';
      port = setPort(port, 21050);
      break;
    case 'mariadb':
    case 'mysql':
      connType = 'mysql+pymysql';
      parameters = '?charset=utf8mb4';
      port = setPort(port, 3306);
      break;
    case 'mssql':
      connType = 'mssql+pymssql';
      port = setPort(port, 135);
      break;
    case 'mongobi':
      connType = 'mongobi+pymysql';
      port = setPort(port, 3307);
      break;
    case 'oracle':
      connType = 'oracle';
      port = setPort(port, 1521);
      break;
    case 'postgresql':
      connType = 'postgresql+psycopg2';
      port = setPort(port, 5432);
      break;
    case 'redshift':
      connType = 'redshift+psycopg2';
      port = setPort(port, 5439);
      break;
    case 'rockset':
      connType = 'rockset';
      return buildRocksetConnectionUrl(api_key, region);
    case 'snowflake':
      return buildSnowflakeConnectionUri(username, password, database, account, schema, warehouse, role);
    case 'vertica':
      connType = 'vertica+vertica_python';
      port = setPort(port, 5433);
      break;
  }

  return (
    connType + '://' +
    username + (password ? (':' + password) : '') + '@' +
    hostname + (port ? (':' + port) : '') + '/' + database + parameters
  );
}

export function parseSqlUri(uri: string | undefined, queryString?: boolean): IDatabaseUri | null {
  if (!uri) {
    return null;
  }

  return parseUrl(uri, queryString || false) as IDatabaseUri;
}

export function wrapEncryptedExtra(encryptedExtra: any, property: string) {
  /*
   * This is used for BigQuery connection
   */
  encryptedExtra = JSON.parse(encryptedExtra);

  if (encryptedExtra.hasOwnProperty(property)) {
    return JSON.stringify(encryptedExtra);
  } else {
    return JSON.stringify({
      [`${property}`]: encryptedExtra
    })
  }
}

export function isNullUndefEmptyStr(obj: any): boolean | undefined {
  for (const key in obj) {
    if (obj[key] !== undefined && obj[key] != "" && obj[key] != null)
      return false;
  }
  return true;
}
